// src/components/Sidebar.tsx
'use client';

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from "@/lib/supabase";
import {
  Home, Apple, Dumbbell, Trophy, Settings, LogOut,
  CalendarDays, Utensils
} from 'lucide-react';

export default function Sidebar() {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const currentPage = 
    location.pathname.includes('/dashboard') ? 'home' :
    location.pathname.includes('/nutrition') ? 'nutrition' :
    location.pathname === '/nutrition-plan' ? 'nutrition-plan' :
    location.pathname === '/training' ? 'training' :
    location.pathname === '/training-program' ? 'training-program' :
    location.pathname.includes('/leaderboard') ? 'leaderboard' :
    location.pathname.includes('/settings') ? 'settings' :
    location.pathname === '/billing' ? 'billing' : 'home';

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isExpanded = !isMobile && isHovered;

  return (
    <aside 
      className={`bg-[#ffffff] border-r border-[#e2e8f0] flex flex-col justify-between py-6 transition-all duration-300 flex-shrink-0 ${
        isExpanded ? 'w-64' : 'w-16 md:w-20'
      }`}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => !isMobile && setIsHovered(false)}
    >
      <nav className="flex flex-col gap-4 px-2 md:px-4">
        <Link to="/dashboard" data-tour="home" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Home className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'home' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && <span className={`font-medium ${currentPage === 'home' ? 'text-green-400' : 'text-[#0f172a]'}`}>Home</span>}
        </Link>
        <Link to="/nutrition" data-tour="nutrition" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Apple className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'nutrition' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && <span className={`font-medium ${currentPage === 'nutrition' ? 'text-green-400' : 'text-[#0f172a]'}`}>Nutrition</span>}
        </Link>
        <Link to="/nutrition-plan" data-tour="nutrition-plan" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Utensils className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'nutrition-plan' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && (
            <span className={`font-medium ${currentPage === 'nutrition-plan' ? 'text-green-400' : 'text-[#0f172a]'}`}>
              Nutrition Plan
            </span>
          )}
        </Link>
        <Link to="/training" data-tour="training" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Dumbbell className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'training' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && <span className={`font-medium ${currentPage === 'training' ? 'text-green-400' : 'text-[#0f172a]'}`}>Training</span>}
        </Link>
        <Link to="/training-program" data-tour="training-program" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <CalendarDays className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'training-program' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && (
            <span className={`font-medium ${currentPage === 'training-program' ? 'text-green-400' : 'text-[#0f172a]'}`}>
              Training Program
            </span>
          )}
        </Link>
        <Link to="/leaderboard" data-tour="leaderboard" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Trophy className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'leaderboard' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && <span className={`font-medium ${currentPage === 'leaderboard' ? 'text-green-400' : 'text-[#0f172a]'}`}>Leaderboard</span>}
        </Link>
        <Link to="/settings" data-tour="settings" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <Settings className={`w-5 h-5 md:w-6 md:h-6 ${currentPage === 'settings' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} />
          {isExpanded && <span className={`font-medium ${currentPage === 'settings' ? 'text-green-400' : 'text-[#0f172a]'}`}>Settings</span>}
        </Link>
        <Link to="/billing" className={`flex items-center gap-3 ${isExpanded ? 'justify-start' : 'justify-center'}`}>
          <svg className={`w-5 h-5 md:w-6 md:h-6 ${location.pathname === '/billing' ? 'text-green-400' : 'text-gray-500 hover:text-green-400'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M7 8h10M7 12h7" />
          </svg>
          {isExpanded && <span className={`font-medium ${location.pathname === '/billing' ? 'text-green-400' : 'text-[#0f172a]'}`}>Billing</span>}
        </Link>
      </nav>
      <div className="flex flex-col items-center gap-6 px-2 md:px-4">
        <LogOut 
          className="w-5 h-5 text-gray-500 hover:text-red-500 cursor-pointer" 
          onClick={handleLogout} 
        />
      </div>
    </aside>
  );
}
